import React from 'react';

import { Helpers } from '../../core';
import JsonLd from '../../shared/components/json-ld';
import { useGetProductPrice } from '../../hooks/useProductPrice';
import {
    getJsonLdImageArrayData,
    createJsonLdBrand,
    createJsonLdAggregateRating,
    createJsonLdOffer,
} from '../../shared/components/json-ld-utils';

// Legacy export - delegates to shared utility
export const getProductJsonLdImageArrayData = (images, product) => getJsonLdImageArrayData(images, product);

// Legacy function - delegates to shared utility
const getProductJsonLdBrand = ({ brand, facet_subbrand }) => createJsonLdBrand({ brand, facet_subbrand });

// Legacy function - delegates to shared utility
const getProductJsonLdRatings = (name, rating, numReviews) => {
    return createJsonLdAggregateRating({
        name,
        rating,
        numReviews,
        itemType: 'IndividualProduct',
    });
};

// Legacy export - delegates to shared utility
export const getProductJsonLdOffers = ({
    sku,
    linkUrl,
    productUrl,
    instock,
    salePrice,
    listPrice,
    isOOS,
    price,
    shipCutOffTime: cutoffTime,
}) => {
    return createJsonLdOffer({
        sku,
        linkUrl,
        productUrl,
        instock,
        salePrice,
        listPrice,
        isOOS,
        price,
        shipCutOffTime: cutoffTime,
    });
};

const getLastPart = (path, delimiter = '/') => {
    if (typeof path !== 'string' || !path) {
        return '';
    }
    const cleanPath = path.split(/[?#]/)[0];
    const normalizePath = cleanPath.endsWith('/') && cleanPath.length > 1 ? cleanPath.slice(0, -1) : cleanPath;
    const index = normalizePath.lastIndexOf(delimiter);
    return index === -1 ? normalizePath : normalizePath.slice(index + 1);
};

const getReplacedByJsonLd = (ctaReplacementURL, brand, facet_subbrand) => {
    if (ctaReplacementURL === '' || ctaReplacementURL === undefined || typeof ctaReplacementURL !== 'string')
        return null;
    const rawLastPart = getLastPart(ctaReplacementURL);
    const decodeLastPart = (() => {
        try {
            return decodeURIComponent(rawLastPart);
        } catch {
            return rawLastPart;
        }
    })();

    return {
        replacedBy: {
            '@type': 'Product',
            '@id': Helpers.getAbsoluteRedirectPath(ctaReplacementURL),
            name: Helpers.removeKebabCase({ str: decodeLastPart }),
            ...getProductJsonLdBrand({ brand, facet_subbrand }),
        },
    };
};

const IGNORE_KEYS = ['@context'];
//parse stuctured data from BV and merges without duplicates
const cleanDefaultStructuredData = (product, structuredData) => {
    return Object.entries(structuredData || {}).reduce((acc, [, value]) => {
        //omit ignore keys from value
        const cleanValue =
            value && typeof value === 'object'
                ? Object.fromEntries(Object.entries(value).filter(([k]) => !IGNORE_KEYS.includes(k)))
                : value;
        acc = { ...acc, ...cleanValue };
        return acc;
    }, {});
};

// note: products from swiftype have a slightly different syntax.
// For example, prdOverview would be in the root of the product rather than in the attributes field
export const getProductJsonLdData = (
    {
        name,
        image,
        sku,
        rating,
        numReviews,
        gtin,
        brand,
        facet_subbrand,
        listPrice,
        salePrice,
        attributes,
        prdOverview,
        linkUrl,
        productUrl,
        instock,
        isOOS,
        price,
        mfpartnumber,
        itemcondition,
        shipCutOffTime,
        sameAsProducts,
        ctaReplacementURL,
        eol,
    },
    seo,
    structuredData,
) => {
    const { prdOverview: attributeProductOverview } = attributes || {};
    const { description } = seo || {};
    const productDescription = description || attributeProductOverview || prdOverview;
    const sameAsArray =
        typeof sameAsProducts === 'string'
            ? sameAsProducts
                  .split(',')
                  .map(s => s?.trim())
                  .filter(s => !!s)
            : sameAsProducts;
    const jsonLdData = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: name,
        sku: sku,
        image,
        ...((gtin && { gtin: gtin }) || {}),
        ...((mfpartnumber && { mpn: mfpartnumber }) || {}),
        ...(productDescription && {
            description: productDescription,
        }),
        ...(itemcondition && { itemCondition: `https://schema.org/${itemcondition}Condition` }),
        ...getProductJsonLdBrand({ brand, facet_subbrand }),
        ...(Array.isArray(sameAsArray) && sameAsArray.length > 0
            ? {
                  sameAs: sameAsArray,
              }
            : {}),
        ...getProductJsonLdRatings(name, rating, numReviews),
        ...getProductJsonLdOffers({
            sku,
            instock,
            isOOS,
            linkUrl,
            productUrl,
            salePrice,
            listPrice,
            price,
            shipCutOffTime,
        }),
        ...getReplacedByJsonLd(ctaReplacementURL, brand, facet_subbrand),
        ...(eol && { discontinued: true }),
        ...cleanDefaultStructuredData({ name, description: productDescription }, structuredData),
    };
    return jsonLdData;
};

export default ({ product, price, enableImageDetails }) => {
    if (!product) return null;

    const priceObj = useGetProductPrice(!price ? product : undefined) || price;

    let parsedImages = getProductJsonLdImageArrayData(
        product.images && product.images?.length >= 1 ? product.images : Helpers.getDefaultImagesFromPrice(priceObj),
        enableImageDetails ? product : null,
    );
    let { image } = parsedImages || {};

    return product.name ? (
        <JsonLd
            type="product"
            data={getProductJsonLdData({ price: priceObj, ...product, image })}
            shouldNotUseDefaults={true}
        />
    ) : null;
};
